// Admin API Service Configuration

/**
 * Admin API Base URL configuration
 */
export const ADMIN_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dev-sakti.gbs.id/api/insight';

/**
 * Admin API endpoints configuration
 */
export const ADMIN_API_CONFIG = {
  // Base URLs
  BASE_URL: ADMIN_API_BASE_URL,

  // Admin endpoints
  ENDPOINTS: {
    // Admin permission check
    CHECK_PERMISSION: '/admin/check-permission',
    USER_INFO: '/admin/user-info',

    // Categories
    CATEGORIES: '/admin/categories',
    CATEGORY_BY_ID: (id: number) => `/admin/categories/${id}`,
    CATEGORY_RESTORE: (id: number) => `/admin/categories/${id}/restore`,

    // Penalties
    PENALTIES: '/admin/penalties',
    PENALTY_BY_ID: (id: number) => `/admin/penalties/${id}`,
    PENALTY_RESTORE: (id: number) => `/admin/penalties/${id}/restore`,

    // Estates
    ESTATES: '/admin/estates',
    ESTATE_BY_ID: (id: number) => `/admin/estates/${id}`,
    ESTATE_RESTORE: (id: number) => `/admin/estates/${id}/restore`,

    // Divisi
    DIVISIS: '/admin/divisis',
    DIVISI_BY_ID: (id: number) => `/admin/divisis/${id}`,
    DIVISI_RESTORE: (id: number) => `/admin/divisis/${id}/restore`,

    // Bloks
    BLOKS: '/admin/bloks',
    BLOK_BY_ID: (id: number) => `/admin/bloks/${id}`,
    BLOK_RESTORE: (id: number) => `/admin/bloks/${id}/restore`,

    // Job Levels
    JOB_LEVELS: '/admin/job-levels',
    JOB_LEVEL_BY_ID: (id: number) => `/admin/job-levels/${id}`,
    JOB_LEVEL_RESTORE: (id: number) => `/admin/job-levels/${id}/restore`,

    // Foreman Groups
    FOREMAN_GROUPS: '/admin/foreman-groups',
    FOREMAN_GROUP_BY_ID: (id: number) => `/admin/foreman-groups/${id}`,
    FOREMAN_GROUP_RESTORE: (id: number) => `/admin/foreman-groups/${id}/restore`,

    // Employees
    EMPLOYEES: '/admin/employees',
    EMPLOYEE_BY_ID: (id: number) => `/admin/employees/${id}`,
    EMPLOYEE_RESTORE: (id: number) => `/admin/employees/${id}/restore`,

    // Blok Inspeksi (Special endpoint)
    BLOK_INSPEKSI_LIST: '/admin/blok-inspeksi/list',
    BLOK_INSPEKSI_SYNC_STATUS: (uuid: string) => `/admin/blok-inspeksi/${uuid}/sync-status`,

    // Legacy inspeksi endpoint (from existing component)
    INSPEKSI_LIST: '/inspeksi/list',
    INSPEKSI_SYNC_STATUS: (uuid: string) => `/inspeksi/blok-inspeksi/${uuid}/sync-status`,

    // Login Requests
    LOGIN_REQUESTS: '/auth/login-request',
    LOGIN_REQUEST_PENDING: '/auth/login-request/pending',
    LOGIN_REQUEST_APPROVE: (id: number) => `/auth/login-request/${id}/approve`,
    LOGIN_REQUEST_REJECT: (id: number) => `/auth/login-request/${id}/reject`,
  },

  // Request configuration
  TIMEOUT: 30000, // 30 seconds

  // Retry configuration
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000 // 1 second
};

/**
 * Admin Dashboard configuration
 */
export const ADMIN_DASHBOARD_CONFIG = {
  // Pagination defaults
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 5,

  // Table settings
  TABLE: {
    ITEMS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
    DEFAULT_SORT_DIRECTION: 'desc' as const,
    DEFAULT_SORT_FIELD: 'updated_at'
  },

  // Auto refresh settings
  AUTO_REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes

  // Date format settings
  DATE_FORMATS: {
    DISPLAY: 'DD/MM/YYYY',
    API: 'YYYY-MM-DD',
    INPUT: 'YYYY-MM-DD'
  }
};

/**
 * Admin Theme configuration
 */
export const ADMIN_THEME_CONFIG = {
  // Color schemes
  COLORS: {
    PRIMARY: '#3182CE',      // Blue
    SECONDARY: '#2B6CB0',    // Dark blue
    SUCCESS: '#38A169',      // Green
    WARNING: '#D69E2E',      // Orange
    ERROR: '#E53E3E',        // Red
    INFO: '#3182CE',         // Blue
    GRAY: '#718096'          // Gray
  },

  // Status colors
  STATUS_COLORS: {
    SYNCED: 'green',
    NOT_SYNCED: 'red',
    ACTIVE: 'green',
    INACTIVE: 'gray',
    DISABLED: 'red'
  }
};

/**
 * Admin Error messages
 */
export const ADMIN_ERROR_MESSAGES = {
  NETWORK_ERROR: 'Terjadi kesalahan jaringan. Silakan coba lagi.',
  API_ERROR: 'Terjadi kesalahan pada server. Silakan coba lagi.',
  UNAUTHORIZED: 'Anda tidak memiliki akses untuk melakukan operasi ini.',
  FORBIDDEN: 'Akses ditolak. Hanya admin yang dapat mengakses halaman ini.',
  NOT_FOUND: 'Data tidak ditemukan.',
  VALIDATION_ERROR: 'Data yang dikirim tidak valid.',
  TIMEOUT: 'Permintaan memakan waktu terlalu lama. Silakan coba lagi.',
  UNKNOWN: 'Terjadi kesalahan yang tidak diketahui. Silakan hubungi administrator.',

  // CRUD specific
  CREATE_ERROR: 'Gagal membuat data baru.',
  UPDATE_ERROR: 'Gagal memperbarui data.',
  DELETE_ERROR: 'Gagal menghapus data.',
  RESTORE_ERROR: 'Gagal mengembalikan data.',
  FETCH_ERROR: 'Gagal memuat data.',
};

/**
 * Admin Success messages
 */
export const ADMIN_SUCCESS_MESSAGES = {
  DATA_LOADED: 'Data berhasil dimuat.',
  DATA_CREATED: 'Data berhasil dibuat.',
  DATA_UPDATED: 'Data berhasil diperbarui.',
  DATA_DELETED: 'Data berhasil dihapus.',
  DATA_RESTORED: 'Data berhasil dikembalikan.',
  SYNC_STATUS_UPDATED: 'Status sinkronisasi berhasil diperbarui.',
};

/**
 * Admin Loading states
 */
export const ADMIN_LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
} as const;

export type AdminLoadingState = typeof ADMIN_LOADING_STATES[keyof typeof ADMIN_LOADING_STATES];
